/*  src/pages/AdminDashboard.jsx  */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import API from '../services/api';
import '../styles/Dashboard.css';

/* helpers -------------------------------------------------- */
const PER_PAGE      = 50;
const PREV_PER_PAGE = 50;
const btn = (bg) => ({
  background: bg, color: '#fff', border: 'none',
  borderRadius: 6, padding: '6px 16px', cursor: 'pointer',
  fontSize: '0.85rem'
});
const fmt = (v) => (v === undefined || v === null || v === '' ? '—' : v);

/* component ------------------------------------------------ */
export default function AdminDashboard() {
  /* ───────── master data ───────── */
  const [data, setData] = useState([]);
  const [viewRow, setViewRow] = useState(null);     // ← details modal

  useEffect(() => {
    API.get('/requests/admin').then(r => setData(Array.isArray(r.data) ? r.data : []));
  }, []);

  /* ───────── ring counters ───────── */
  const counts = useMemo(() => {
    const c = { pending:0, approved:0, rejected:0, out:0, in:0 };
    data.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
    c.total = data.length || 1; // avoid div/0 later
    return c;
  }, [data]);

  const slice = (n,t) => `${(n/t)*440} ${440-(n/t)*440}`;

  /* ============ CURRENT LIST (any status) ============ */
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);

  const filtered = useMemo(() => {
    let arr = data;
    if (status!=='all') arr = arr.filter(r=>r.status===status);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(r =>
        [r.name,r.studentId,r.leaveFor,r.transport,r.email,r.pickUpWith]
          .some(f=>String(f||'').toLowerCase().includes(q)));
    }
    return arr.sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));
  }, [data, status, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageData   = filtered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  /* ============ PREVIOUS (IN / OUT) ============ */
  const [showPrev, setShowPrev] = useState(false);
  const [prevPeriod, setPrevPeriod] = useState('today');     // today | week | month | all
  const [prevDate,   setPrevDate]   = useState('');
  const [prevPage,   setPrevPage]   = useState(1);

  /* helpers for date buckets */
  const today    = new Date(new Date().setHours(0,0,0,0));
  const tomorrow = new Date(today);  tomorrow.setDate(tomorrow.getDate()+1);
  const weekAgo  = new Date(today);  weekAgo.setDate(weekAgo.getDate()-7);
  const monthAgo = new Date(today);  monthAgo.setMonth(monthAgo.getMonth()-1);

  const prevListFiltered = useMemo(() => {
    let arr = data.filter(r => r.status==='in' || r.status==='out');

    /* date filters */
    if (prevDate) {
      const d0 = new Date(prevDate);
      const d1 = new Date(d0); d1.setDate(d1.getDate()+1);
      arr = arr.filter(r=>{
        const d = new Date(r.dateTime);
        return d>=d0 && d<d1;
      });
    } else {
      const keep = d=>(
        prevPeriod==='today' ? d>=today && d<tomorrow :
        prevPeriod==='week'  ? d>=weekAgo :
        prevPeriod==='month' ? d>=monthAgo : true
      );
      arr = arr.filter(r=>keep(new Date(r.dateTime)));
    }

    return arr.sort((a,b)=>new Date(b.dateTime)-new Date(a.dateTime));
  }, [data, prevPeriod, prevDate]);

  const prevTotalPages = Math.max(1, Math.ceil(prevListFiltered.length / PREV_PER_PAGE));
  const prevPageRows   = prevListFiltered.slice((prevPage-1)*PREV_PER_PAGE,
                                                prevPage*PREV_PER_PAGE);

  /* ───────── CSV export (prev list only) ───────── */
  const exportPrevCsv = useCallback(() => {
    if (!prevListFiltered.length) {
      alert('No IN / OUT records to export.'); return;
    }
    const rows = [
      ['Date & Time','Status','Student','ID','Reason','Transport'].join(','),
      ...prevListFiltered.map(r=>[
        new Date(r.dateTime).toLocaleString(),
        r.status, r.name, r.studentId, `"${String(r.leaveFor||'').replace(/"/g,'""')}"`, r.transport
      ].join(','))
    ].join('\n');

    const blob = new Blob([rows],{type:'text/csv;charset=utf-8'});
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href=url; a.download=`prev_in_out_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [prevListFiltered]);

  /* ───────── JSX ───────── */
  return (
    <div className="dashboard-container">

      {/* heading & ring */}
      <div className="dash-head">
        <div>
          <h2 style={{marginBottom:4}}>🗂️ Admin View</h2>
          <p style={{marginTop:0,color:'#555'}}>Read-only overview of every request</p>
        </div>
      </div>

      <div style={{display:'flex',flexWrap:'wrap',gap:24,justifyContent:'center',marginBottom:32}}>
        {/* donut */}
        <div className="card-lite">
          <svg width="220" height="220" viewBox="0 0 160 160">
            <circle cx="80" cy="80" r="70" fill="none" stroke="#e5e7eb" strokeWidth="14"/>
            {['pending','approved','rejected','out','in'].reduce((acc,stat)=>{
              const len = slice(counts[stat], counts.total).split(' ')[0];
              const off = acc.offset;
              acc.offset += (counts[stat]/counts.total)*440;
              acc.elems.push(
                <circle key={stat} cx="80" cy="80" r="70" fill="none"
                  strokeDasharray={`${len} 440`} strokeDashoffset={-off}
                  strokeWidth="14" strokeLinecap="round"
                  stroke={ stat==='pending' ? '#facc15'
                        : stat==='approved'? '#22c55e'
                        : stat==='rejected'? '#ef4444'
                        : stat==='out'     ? '#fb923c'
                        : '#6366f1'} />
              );
              return acc;
            },{offset:0,elems:[]}).elems}
            <text x="50%" y="50%" textAnchor="middle" dy="0.3em"
                  fontSize="24" fontWeight="600">{counts.total}</text>
          </svg>
        </div>

        {/* legend */}
        <div className="card-lite" style={{alignSelf:'center'}}>
          {[
            ['Pending',counts.pending,'#facc15'],
            ['Approved',counts.approved,'#22c55e'],
            ['Rejected',counts.rejected,'#ef4444'],
            ['Out',counts.out,'#fb923c'],
            ['In',counts.in,'#6366f1']
          ].map(([lbl,val,col])=>(
            <div key={lbl} style={{display:'flex',alignItems:'center',marginBottom:8}}>
              <span style={{width:14,height:14,borderRadius:'50%',background:col,marginRight:8}}/>
              <span style={{flex:1}}>{lbl}</span><strong>{val}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* ---------- CURRENT FILTER ROW ---------- */}
      <div className="search-filter">
        <select value={status} onChange={e=>{setStatus(e.target.value); setPage(1);}}>
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="out">Out</option>
          <option value="in">In</option>
        </select>
        <input
          placeholder="Search name, ID, reason, email…"
          value={search}
          onChange={e=>{setSearch(e.target.value); setPage(1);}}
        />
      </div>

      {/* ---------- CURRENT TABLE ---------- */}
      <div className="table-container prettified">
        <h3 style={{margin:'8px 0'}}>All Current Requests</h3>
        <table>
          <thead>
            <tr>
              <th>Date &amp; Time</th>
              <th>Status</th>
              <th>Student</th>
              <th>ID</th>
              <th>Reason</th>
              <th>Transport</th>
              <th>Details</th>  {/* NEW */}
            </tr>
          </thead>
          <tbody>
            {pageData.length ? pageData.map(r=>(
              <tr key={r._id}>
                <td>{new Date(r.dateTime).toLocaleString()}</td>
                <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>
                <td>{r.name}</td>
                <td>{r.studentId}</td>
                <td style={{maxWidth:260,wordBreak:'break-word'}}>{fmt(r.leaveFor)}</td>
                <td>{fmt(r.transport)}</td>
                <td>
                  <button className="view-btn" onClick={()=>setViewRow(r)}>View</button>
                </td>
              </tr>
            )) : (
              <tr><td colSpan={7} style={{textAlign:'center',padding:16}}>No records.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1}>Prev</button>
        <span className="page-info">Page {page} of {totalPages}</span>
        <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
      </div>

      {/* ---------- TOGGLE PREVIOUS (IN / OUT) ---------- */}
      <div style={{margin:'20px 0'}}>
        <button className="action-btn"
                onClick={()=>setShowPrev(o=>!o)}>
          {showPrev ? 'Hide Previous (IN / OUT)' : 'Show Previous (IN / OUT)'}
        </button>
      </div>

      {showPrev && (
        <>
          {/* quick range + date picker + export */}
          <div className="search-filter" style={{marginTop:12}}>
            {['today','week','month','all'].map(p=>(
              <button key={p}
                className={`period-btn ${prevPeriod===p?'active':''}`}
                onClick={()=>{setPrevPeriod(p);setPrevDate('');setPrevPage(1);}}>
                {{today:'Today',week:'Week',month:'Month',all:'All'}[p]}
              </button>
            ))}
            <input type="date" value={prevDate}
              onChange={e=>{setPrevDate(e.target.value); setPrevPage(1);}}
              disabled={prevPeriod!=='all'} />
            <button className="action-btn green" style={{marginLeft:'auto'}}
                    onClick={exportPrevCsv}>
              ⬇︎ Export CSV
            </button>
          </div>

          {/* previous table */}
          <div className="table-container prettified">
            <h3 style={{margin:'8px 0'}}>Previous (IN / OUT)</h3>
            <table>
              <thead>
                <tr>
                  <th>Date &amp; Time</th>
                  <th>Status</th>
                  <th>Student</th>
                  <th>ID</th>
                  <th>Reason</th>
                  <th>Transport</th>
                  <th>Details</th> {/* NEW */}
                </tr>
              </thead>
              <tbody>
                {prevPageRows.length ? prevPageRows.map(r=>(
                  <tr key={r._id}>
                    <td>{new Date(r.dateTime).toLocaleString()}</td>
                    <td><span className={`status-badge status-${r.status}`}>{r.status}</span></td>
                    <td>{r.name}</td>
                    <td>{r.studentId}</td>
                    <td style={{maxWidth:260,wordBreak:'break-word'}}>{fmt(r.leaveFor)}</td>
                    <td>{fmt(r.transport)}</td>
                    <td>
                      <button className="view-btn" onClick={()=>setViewRow(r)}>View</button>
                    </td>
                  </tr>
                )) : (
                  <tr><td colSpan={7} style={{textAlign:'center',padding:16}}>No IN / OUT records.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button onClick={()=>setPrevPage(p=>Math.max(1,p-1))} disabled={prevPage===1}>Prev</button>
            <span className="page-info">Page {prevPage} of {prevTotalPages}</span>
            <button onClick={()=>setPrevPage(p=>Math.min(prevTotalPages,p+1))} disabled={prevPage===prevTotalPages}>Next</button>
          </div>
        </>
      )}

      {/* ===== Details modal (sleek, shared styles) ===== */}
      {viewRow && (
        <div className="modal-overlay details-modal">
          <div className="modal sleek">
            <div className="modal-header details-header">
              <h3>Leave Request Details</h3>
              <button
                className="icon-close"
                onClick={() => setViewRow(null)}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            {/* Top summary strip */}
            <div className="summary-strip">
              <div className="summary-left">
                <div className="summary-title">
                  <span className="student-name">{fmt(viewRow.name)}</span>
                  <span className="chip-id">ID: {fmt(viewRow.studentId)}</span>
                </div>
                <div className="summary-sub">
                  {fmt(viewRow.email)}
                  {viewRow.hostelType ? (
                    <span className="chip-hostel">{viewRow.hostelType}</span>
                  ) : null}
                </div>
              </div>

              <div className="summary-right">
                <span className={`status-badge status-${viewRow.status}`}>{viewRow.status}</span>
              </div>
            </div>

            <div className="modal-body">
              <div className="kv-grid">
                <div className="kv">
                  <div className="k">Reason</div>
                  <div className="v prewrap">{fmt(viewRow.leaveFor)}</div>
                </div>

                <div className="kv">
                  <div className="k">Leaving With</div>
                  <div className="v">{fmt(viewRow.pickUpWith)}</div>
                </div>

                <div className="kv">
                  <div className="k">Transport</div>
                  <div className="v">{fmt(viewRow.transport)}</div>
                </div>

                <div className="kv">
                  <div className="k">Vehicle No</div>
                  <div className="v">{fmt(viewRow.vehicleNo)}</div>
                </div>

                <div className="kv">
                  <div className="k">Driver Name</div>
                  <div className="v">{fmt(viewRow.driverName)}</div>
                </div>

                <div className="kv">
                  <div className="k">Date &amp; Time</div>
                  <div className="v mono">{new Date(viewRow.dateTime).toLocaleString()}</div>
                </div>

                <div className="kv full">
                  <div className="k">Warden Comment</div>
                  <div className="v note prewrap">{fmt(viewRow.wardenComment)}</div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="close-btn" onClick={() => setViewRow(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
